import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FilterType, ReportType, ShapeType } from '../../reportsTypes';
import { StepConfig, useStepper } from '@ais-ui/ui-core';
import { DateRange } from 'react-day-picker';
import {
  GeoJsonDictionary,
  ReportFilterType,
  ShapeDictionaryType,
} from '../../utils';
import { FieldValues, UseFormReturn } from 'react-hook-form';
import { useDatabaseRange } from '@ais-ui/api';
import { Ship } from '../utils/createDynamicFilterZodSchema';
import { useSettings } from '../../../Settings/SettingsProvider';

interface MutateReportContextProps {
  showDialog: boolean;
  setShowDialog: React.Dispatch<React.SetStateAction<boolean>>;
  chosenReportType: ReportType | null | undefined;
  setChosenReportType: React.Dispatch<
    React.SetStateAction<ReportType | null | undefined>
  >;
  steps: StepConfig[];
  setSteps: React.Dispatch<React.SetStateAction<StepConfig[]>>;
  reportTemplateName: string;
  setReportTemplateName: React.Dispatch<React.SetStateAction<string>>;
  firstReportName: string;
  setFirstReportName: React.Dispatch<React.SetStateAction<string>>;
  dateRange: DateRange | undefined;
  setDateRange: React.Dispatch<React.SetStateAction<DateRange | undefined>>;
  filter: ReportFilterType | null;
  setFilter: React.Dispatch<React.SetStateAction<ReportFilterType | null>>;
  shapeDictionary: ShapeDictionaryType;
  setShapeDictionary: React.Dispatch<React.SetStateAction<ShapeDictionaryType>>;
  miscellaneous: Record<string, unknown>;
  setMiscellaneous: React.Dispatch<
    React.SetStateAction<Record<string, unknown>>
  >;
  stepperProps: ReturnType<typeof useStepper>;
  currentStep: StepConfig | null;
  setCurrentStep: React.Dispatch<React.SetStateAction<StepConfig | null>>;
  resetSteps: () => void;
  instantShape: boolean;
  setInstantShape: React.Dispatch<React.SetStateAction<boolean>>;
  shapeForm: UseFormReturn<FieldValues> | null;
  setShapeForm: React.Dispatch<
    React.SetStateAction<UseFormReturn<FieldValues> | null>
  >;
  shapeTab: ShapeType;
  setShapeTab: React.Dispatch<React.SetStateAction<ShapeType>>;
  instantShapeData: GeoJsonDictionary;
  setInstantShapeData: React.Dispatch<React.SetStateAction<GeoJsonDictionary>>;
  activeStep: number;
  startDatetimeThreshold?: Date;
  endDatetimeThreshold?: Date;
  ships: Ship[];
  setShips: React.Dispatch<React.SetStateAction<Ship[]>>;
  shipAttribute: 'colorBy' | 'filterBy';
  setShipAttribute: React.Dispatch<
    React.SetStateAction<'colorBy' | 'filterBy'>
  >;
  createMode: 'wizard' | 'clone';
  setCreateMode: React.Dispatch<React.SetStateAction<'wizard' | 'clone'>>;
  // Add shared form state for both wizard and quick create
  email: string[];
  setEmail: React.Dispatch<React.SetStateAction<string[]>>;
  phone: string;
  setPhone: React.Dispatch<React.SetStateAction<string>>;
  countryCode: string;
  setCountryCode: React.Dispatch<React.SetStateAction<string>>;
  selectedGroups: unknown[];
  setSelectedGroups: React.Dispatch<React.SetStateAction<unknown[]>>;
  filterTab: FilterType;
  setFilterTab: React.Dispatch<React.SetStateAction<FilterType>>;
  activeTab: 'wizard' | 'quick';
  setActiveTab: React.Dispatch<React.SetStateAction<'wizard' | 'quick'>>;
}

const MutateReportContext = createContext<MutateReportContextProps | null>(
  null
);

export const useMutateReportContext = () => {
  const context = useContext(MutateReportContext);
  if (!context) {
    throw new Error(
      'useMutateReportContext must be used within a MutateReportProvider'
    );
  }
  return context;
};

// Keep legacy export for backward compatibility
export const useCreateReportContext = useMutateReportContext;

interface MutateReportProviderProps {
  startDatetimeThreshold?: Date;
  endDatetimeThreshold?: Date;
  children: ReactNode;
}

const baseSteps = [
  { label: 'Name' },
  { label: 'Period' },
] satisfies StepConfig[];

const getDefaultDateRange = (): DateRange => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);

  const result = {
    from: new Date(
      Date.UTC(
        yesterday.getFullYear(),
        yesterday.getMonth(),
        yesterday.getDate() - 1,
        0,
        0,
        0,
        0
      )
    ),
    to: new Date(
      Date.UTC(
        yesterday.getFullYear(),
        yesterday.getMonth(),
        yesterday.getDate(),
        23,
        59,
        59,
        999
      )
    ),
  };

  return result;
};

export const MutateReportProvider: React.FC<MutateReportProviderProps> = ({
  startDatetimeThreshold,
  endDatetimeThreshold,
  children,
}) => {
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const { settings } = useSettings();

  const { data: databaseRange } = useDatabaseRange();

  // Read default template mode from settings
  const defaultTemplateMode =
    settings['ribbonPlugin.settings']?.report_default_template || 'wizard';

  // Use database range values as fallbacks for undefined thresholds
  const effectiveStartDatetimeThreshold = useMemo(
    () =>
      startDatetimeThreshold !== undefined
        ? startDatetimeThreshold
        : databaseRange?.database.begin,
    [startDatetimeThreshold, databaseRange]
  );

  const effectiveEndDatetimeThreshold = useMemo(
    () =>
      endDatetimeThreshold !== undefined
        ? endDatetimeThreshold
        : databaseRange?.database.end,
    [endDatetimeThreshold, databaseRange]
  );

  const [chosenReportType, setChosenReportType] = useState<
    ReportType | null | undefined
  >(null);
  const [steps, setSteps] = useState<StepConfig[]>(baseSteps);

  const [reportTemplateName, setReportTemplateName] = useState<string>('');
  const [firstReportName, setFirstReportName] = useState<string>('');

  const [dateRange, setDateRange] = useState<DateRange | undefined>(
    getDefaultDateRange()
  );
  const [shapeTab, setShapeTab] = useState<ShapeType>('shape');
  const [filterTab, setFilterTab] = useState<FilterType>('normal');
  const [filter, setFilter] = useState<ReportFilterType | null>(null);
  const [ships, setShips] = useState<Ship[]>([]);
  const [shipAttribute, setShipAttribute] = useState<'colorBy' | 'filterBy'>(
    'filterBy'
  );

  // Sync ships context whenever filter context changes for ship-type filters
  useEffect(() => {
    // Check if filter is an array of ships (filterType === 'ship')
    if (Array.isArray(filter) && filter.length > 0) {
      // Validate that the first element looks like a Ship object
      const firstItem = filter[0];
      if (
        firstItem &&
        typeof firstItem === 'object' &&
        'mmsi' in firstItem &&
        'name' in firstItem
      ) {
        setShips(filter as Ship[]);
      }
    }
  }, [filter]);

  // The shapeDictionary is used to store the data which is used in the shapes
  // step in the context of reports.
  const [shapeDictionary, setShapeDictionary] =
    useState<ShapeDictionaryType>(null);

  const [instantShapeData, setInstantShapeData] = useState<GeoJsonDictionary>(
    {}
  );
  // Here we are listening for the instantShape event from useDrawFromDialog.ts
  useEffect(() => {
    const handleInstantShapeEvent = (event: Event) => {
      const detail = (event as CustomEvent<GeoJsonDictionary>).detail;
      setInstantShapeData((prevData) => ({ ...prevData, ...(detail ?? {}) }));
    };
    window.addEventListener('instantShape', handleInstantShapeEvent);

    return () => {
      window.removeEventListener('instantShape', handleInstantShapeEvent);
    };
  }, [shapeTab]);

  const [miscellaneous, setMiscellaneous] = useState<Record<string, unknown>>(
    {}
  );

  const [instantShape, setInstantShape] = useState(false);

  // Add shared form state for both wizard and quick create
  const [email, setEmail] = useState<string[]>([]);
  const [phone, setPhone] = useState<string>('');
  const [countryCode, setCountryCode] = useState<string>('');
  const [selectedGroups, setSelectedGroups] = useState<unknown[]>([]);

  const stepperProps = useStepper({
    initialStep: 0,
    steps,
  });

  const stepperResetRef = useRef(stepperProps.resetSteps);

  useEffect(() => {
    stepperResetRef.current = stepperProps.resetSteps;
  }, [stepperProps.resetSteps]);

  const activeStep = useMemo(
    () => stepperProps.activeStep,
    [stepperProps.activeStep]
  );

  const [currentStep, setCurrentStep] = useState<StepConfig | null>(null);
  const [shapeForm, setShapeForm] = useState<UseFormReturn<FieldValues> | null>(
    null
  );
  const [createMode, setCreateMode] = useState<'wizard' | 'clone'>('wizard');
  const [activeTab, setActiveTab] = useState<'wizard' | 'quick'>(
    defaultTemplateMode
  );

  const resetSteps = useCallback(() => {
    setChosenReportType(null);

    setSteps([]);

    setReportTemplateName('');
    setFirstReportName('');

    setDateRange(getDefaultDateRange());

    setFilter(null);
    setShapeDictionary(null);
    setMiscellaneous({});
    setShapeForm(null);
    setShapeTab('shape');
    setFilterTab('normal');

    // Reset shared form fields
    setEmail([]);
    setPhone('');
    setCountryCode('');
    setSelectedGroups([]);

    // Reset to default tab
    setActiveTab(defaultTemplateMode);

    stepperResetRef.current();
  }, [
    defaultTemplateMode,
    setChosenReportType,
    setSteps,
    setReportTemplateName,
    setFirstReportName,
    setDateRange,
    setFilter,
    setShapeDictionary,
    setMiscellaneous,
    setShapeTab,
    setFilterTab,
    setEmail,
    setPhone,
    setCountryCode,
    setSelectedGroups,
  ]);

  useEffect(() => {
    const tempSteps: StepConfig[] = [...baseSteps];
    if (!chosenReportType) {
      setSteps([]);
      return;
    }

    if (chosenReportType.ships) {
      tempSteps.push({
        label: 'Ships',
      });
    }

    if (chosenReportType.filter) {
      tempSteps.push({
        label: 'Filter',
      });
    }

    if (chosenReportType.shape) {
      tempSteps.push({
        label: 'Shape',
      });
    }

    if (chosenReportType.miscellaneous) {
      tempSteps.push({
        label: 'Miscellaneous',
      });
    }

    setSteps(tempSteps);
  }, [chosenReportType]);

  // Initialize miscellaneous fields with default values when report type is chosen
  useEffect(() => {
    if (!chosenReportType || !chosenReportType.miscellaneous) {
      return;
    }

    const miscellaneousFields = chosenReportType.miscellaneous as Record<
      string,
      { defaultValue?: any; fieldType: string }
    >;

    const defaults: Record<string, any> = {};
    Object.keys(miscellaneousFields).forEach((key) => {
      const field = miscellaneousFields[key];
      if (field.defaultValue !== undefined) {
        defaults[key] = field.defaultValue;
      }
    });

    // Only set if we have defaults to set and miscellaneous is currently empty
    if (
      Object.keys(defaults).length > 0 &&
      Object.keys(miscellaneous).length === 0
    ) {
      setMiscellaneous(defaults);
    }
  }, [chosenReportType]);

  // Initialize filterTab to 'normal' if available, otherwise the first available filter type
  useEffect(() => {
    if (!chosenReportType || !chosenReportType.filter) {
      return;
    }

    const filterFields = chosenReportType.filter as Record<string, any>;

    // Set filterTab to 'normal' if available, otherwise pick the first available type
    if (filterFields.normal) {
      setFilterTab('normal');
    } else if (filterFields.ship) {
      setFilterTab('ship');
    } else if (filterFields.shiplist) {
      setFilterTab('shiplist');
    } else if (filterFields.combined) {
      setFilterTab('combined');
    }

    // NOTE: Don't reset filter here! It interferes with programmatic filter setting
    // (like when creating a ship report from the target card).
    // The filter is already reset in resetSteps() when the user manually changes report types.
  }, [chosenReportType]);

  useEffect(() => {
    if (steps.length === 0) {
      setCurrentStep(null);
      return;
    }
    setCurrentStep(steps[stepperProps.activeStep]);
  }, [stepperProps, steps]);

  const value = {
    showDialog,
    setShowDialog,
    chosenReportType,
    setChosenReportType,
    steps,
    setSteps,
    reportTemplateName,
    setReportTemplateName,
    firstReportName,
    setFirstReportName,
    dateRange,
    setDateRange,
    filter,
    setFilter,
    shapeDictionary,
    setShapeDictionary,
    miscellaneous,
    setMiscellaneous,
    stepperProps,
    currentStep,
    setCurrentStep,
    resetSteps,
    instantShape,
    setInstantShape,
    shapeForm,
    setShapeForm,
    shapeTab,
    setShapeTab,
    instantShapeData,
    setInstantShapeData,
    activeStep,
    startDatetimeThreshold: effectiveStartDatetimeThreshold,
    endDatetimeThreshold: effectiveEndDatetimeThreshold,
    ships,
    setShips,
    shipAttribute,
    setShipAttribute,
    createMode,
    setCreateMode,
    // Shared form state for both wizard and quick create
    email,
    setEmail,
    phone,
    setPhone,
    countryCode,
    setCountryCode,
    selectedGroups,
    setSelectedGroups,
    filterTab,
    setFilterTab,
    activeTab,
    setActiveTab,
  };

  return (
    <MutateReportContext.Provider value={value}>
      {children}
    </MutateReportContext.Provider>
  );
};

// Keep legacy export for backward compatibility
export const CreateReportProvider = MutateReportProvider;
